import { useAuth } from '../../../hooks/useAuth';

interface SidebarProps {
  activeSection: string;
  onNavigate: (section: string) => void;
  userRole: 'kullanici' | 'lider' | 'admin';
  collapsed: boolean;
  onToggle: () => void;
  onSignOut: () => void;
}

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-3-line' },
  { id: 'benim-adimim', label: 'Benim Adımım', icon: 'ri-footprint-line' },
  { id: 'ilk4', label: 'İlk 4 Kişim', icon: 'ri-team-line' },
  { id: 'step6-plani', label: 'STEP6 Planım', icon: 'ri-bar-chart-grouped-line' },
  { id: 'step6-nedir', label: 'STEP6 Planı Nedir?', icon: 'ri-lightbulb-line' },
  { id: 'whatsapp', label: 'WhatsApp Aksiyonları', icon: 'ri-whatsapp-line' },
  { id: 'motivasyon', label: 'Motivasyon & Vizyon', icon: 'ri-fire-line' },
  { id: 'lider-videolari', label: 'Lider Videoları', icon: 'ri-play-circle-line' },
  { id: 'ekip-analizi', label: 'Ekip Analizi', icon: 'ri-pie-chart-2-line', liderOnly: true },
  { id: 'risk-merkezi', label: 'Risk Merkezi', icon: 'ri-alarm-warning-line', liderOnly: true },
  { id: 'ayarlar', label: 'Ayarlar', icon: 'ri-settings-3-line' },
];

const roleLabels: Record<string, string> = { kullanici: 'Kullanıcı', lider: 'Lider', admin: 'Admin' };
const roleColors: Record<string, string> = { kullanici: 'text-blue-400', lider: 'text-amber-400', admin: 'text-red-400' };

export default function Sidebar({ activeSection, onNavigate, userRole, collapsed, onToggle, onSignOut }: SidebarProps) {
  const { profile } = useAuth();

  const visibleItems = navItems.filter(item => {
    if (item.liderOnly && userRole === 'kullanici') return false;
    return true;
  });

  return (
    <aside
      className="h-full w-full flex flex-col overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0D0F1A 0%, #0A0B14 100%)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* ── Logo Row ── */}
      <div
        className="flex items-center gap-3 px-4 py-5 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <img
          src="https://public.readdy.ai/ai/img_res/bd011a02-ab2e-435b-9429-0067149db8ff.png"
          alt="STEP6 Logo"
          className="w-8 h-8 rounded-lg object-contain flex-shrink-0"
        />
        {!collapsed && (
          <div className="flex-1 overflow-hidden">
            <div className="text-white font-bold text-sm whitespace-nowrap">STEP6 Network</div>
            <div className="text-xs whitespace-nowrap" style={{ color: '#F5A623' }}>
              Ekip Yönetim Paneli
            </div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
        >
          <i className={`${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-base`} />
        </button>
      </div>

      {/* ── Nav Items ── */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {visibleItems.map(item => {
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 cursor-pointer text-left ${
                isActive ? 'text-white' : 'text-gray-500 hover:text-gray-200 hover:bg-white/5'
              }`}
              style={
                isActive
                  ? {
                      background:
                        'linear-gradient(135deg, rgba(245,166,35,0.18) 0%, rgba(255,107,53,0.1) 100%)',
                      borderLeft: '2px solid #F5A623',
                    }
                  : {}
              }
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base ${isActive ? 'text-amber-400' : ''}`} />
              </div>
              {!collapsed && (
                <span className="flex-1 text-sm font-medium whitespace-nowrap">{item.label}</span>
              )}
              {item.liderOnly && !collapsed && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded-md text-amber-400 font-semibold flex-shrink-0"
                  style={{ background: 'rgba(245,166,35,0.1)' }}
                >
                  L
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* ── User Profile + Sign Out ── */}
      <div className="px-2 pb-4 flex-shrink-0">
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-settings-line text-base text-gray-400" />
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 text-left overflow-hidden">
                <div className={`text-sm font-semibold leading-none mb-0.5 ${roleColors[userRole]}`}>
                  {profile?.full_name || roleLabels[userRole]}
                </div>
                <div className="text-xs text-gray-600 truncate">{roleLabels[userRole]}</div>
              </div>
              <button
                onClick={onSignOut}
                className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer flex-shrink-0 transition-colors hover:bg-red-500/10"
                title="Çıkış Yap"
              >
                <i className="ri-logout-box-r-line text-sm text-gray-400 hover:text-red-400" />
              </button>
            </>
          )}
        </div>
        {collapsed && (
          <button
            onClick={onSignOut}
            className="w-full mt-2 flex items-center justify-center py-2 rounded-xl cursor-pointer transition-colors hover:bg-red-500/10"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
            title="Çıkış Yap"
          >
            <i className="ri-logout-box-r-line text-sm text-gray-400" />
          </button>
        )}
      </div>
    </aside>
  );
}